// The day's schedule on one screen, start times by fields, the way an
// organizer's referee sheet reads. An assigner staffs it from here; a referee
// reads it with their own games marked

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import CheckBox from './CheckBox';
import Select from './Select';
import GameBlockRefs from './GameBlockRefs';
import AssistantPanel from './AssistantPanel';
import { useFirebase } from './FirebaseContextProvider';
import { useModal } from './ModalProvider';
import { useTournamentParts } from '../utils/hooks';
import { gameDivision } from '../utils/gameReport';
import { time24To12H } from '../utils/misc';
import { storageDate } from '../utils/consts';
import { RefereeStatuses } from '../utils/enums';
import './CompactSchedule.scss';

// "First L.": two referees still fit on one line of a narrow column
const shortName = (name) => {
  const words = name.trim().split(/\s+/);
  return words.length > 1 ? `${words[0]} ${words.at(-1)[0]}.` : words[0];
};

const dayLabel = (day) => moment(day).format('ddd, MMM D');

function CompactGame({
  id,
  game,
  fieldName,
  colorLegend,
  acceptedIds,
  myId,
  highlighted,
  canAssign,
}) {
  const { teamsById, refereesById } = useFirebase();
  const { openModal } = useModal();
  const ref = useRef(null);

  const division = gameDivision(game, teamsById);
  const color = colorLegend[division]?.color;
  const team1 = teamsById.get(game.team1)?.name;
  const team2 = teamsById.get(game.team2)?.name;
  const sides =
    team1 || team2
      ? `${team1 || 'TBD'} – ${team2 || 'TBD'}`
      : division || 'TBD';

  // A uid whose account is gone has no name left on the game
  const refs = (game.referees || []).map((uid) => ({
    uid,
    name: refereesById.get(uid)?.name || 'Unknown',
    offRoster: !!acceptedIds && !acceptedIds.has(uid),
  }));
  const needsAttention =
    canAssign && (!refs.length || refs.some((r) => r.offRoster));
  const isMine = !!myId && refs.some((r) => r.uid === myId);

  const assign = () =>
    openModal({
      header: `Assign referees: ${time24To12H(game.start)}, ${fieldName}`,
      body: <GameBlockRefs id={id} game={game} />,
      ancorRef: ref,
    });

  const title = [
    sides,
    ...(sides !== division && division ? [division] : []),
    ...refs.map((r) => r.name + (r.offRoster ? ' (not on roster)' : '')),
  ].join('\n');

  return (
    <div
      ref={ref}
      className={
        'game' +
        (isMine ? ' mine' : '') +
        (needsAttention ? ' attention' : '') +
        (highlighted ? ' highlighted' : '')
      }
      style={color && { borderLeftColor: color, backgroundColor: `${color}1A` }}
      data-game-id={game.id}
      title={title}
      {...(canAssign && {
        role: 'button',
        tabIndex: 0,
        onClick: assign,
        onKeyDown: (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          assign();
        },
      })}
    >
      <p className='sides'>{sides}</p>
      <p className={'refs' + (refs.length ? '' : ' none')}>
        {refs.length
          ? refs.map((r, i) => (
              <span
                key={r.uid}
                className={r.offRoster ? 'off-roster' : undefined}
              >
                {(i > 0 ? ', ' : '') + shortName(r.name)}
              </span>
            ))
          : 'No referees'}
      </p>
    </div>
  );
}

export default function CompactSchedule({ id, colorLegend }) {
  const {
    tournamentsById,
    teamsById,
    user,
    userIsReferee,
    userIsRefereeAssigner,
  } = useFirebase();
  const [searchParams] = useSearchParams();
  // Brackets' "watch in schedule" leaves the game's day here
  const [pickedDay, setPickedDay] = useState(() =>
    localStorage.getItem(storageDate),
  );
  const [onlyMine, setOnlyMine] = useState(false);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);

  const canAssign = userIsRefereeAssigner;
  const myId = userIsReferee ? user?.id : null;
  const highlightId = searchParams.get('highlight');

  const tournament = tournamentsById.get(id);
  const { games, fields, referees: roster, refereesSynced } = tournament;

  useTournamentParts(canAssign ? id : null, ['referees']);
  // Off-roster is a conclusion from absence, so it waits for the server
  const acceptedIds = useMemo(
    () =>
      canAssign && refereesSynced
        ? new Set(
            roster
              .filter((entry) => entry.status === RefereeStatuses.ACCEPTED)
              .map((entry) => entry.id),
          )
        : null,
    [canAssign, refereesSynced, roster],
  );

  const days = useMemo(
    () => [...new Set(games.map((game) => game.date).filter(Boolean))].sort(),
    [games],
  );
  const today = moment().format('YYYY-MM-DD');
  const day = days.includes(pickedDay)
    ? pickedDay
    : (days.find((d) => d >= today) ?? days[0]);

  const pickDay = (label) => {
    const picked = days.find((d) => dayLabel(d) === label);
    setPickedDay(picked);
    localStorage.setItem(storageDate, picked);
  };

  const { columns, rows, legend } = useMemo(() => {
    const fieldIds = new Set(fields.map((field) => field.id));
    const dayGames = games.filter(
      (game) =>
        game.date === day &&
        fieldIds.has(game.field) &&
        (!onlyMine || game.referees?.includes(myId)),
    );

    const usedFields = new Set(dayGames.map((game) => game.field));
    const columns = fields
      .filter((field) => usedFields.has(field.id))
      .toSorted((a, b) => a.sortIndex - b.sortIndex);

    const rows = [...new Set(dayGames.map((game) => game.start))]
      .sort()
      .map((start) => ({
        start,
        games: dayGames.filter((game) => game.start === start),
      }));

    const divisions = new Set(
      dayGames.map((game) => gameDivision(game, teamsById)),
    );
    const legend = Object.entries(colorLegend).filter(([division]) =>
      divisions.has(division),
    );

    return { columns, rows, legend };
  }, [games, fields, day, onlyMine, myId, teamsById, colorLegend]);

  useEffect(() => {
    if (!highlightId) return;
    document
      .querySelector(`.compact-schedule [data-game-id="${highlightId}"]`)
      ?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
  }, [highlightId, day]);

  if (!day) return <p className='compact-schedule-empty'>No games yet</p>;

  const schedule = (
    <div className='compact-schedule'>
      <div className='toolbar'>
        {days.length > 1 ? (
          <Select
            className='day'
            options={days.map(dayLabel)}
            value={dayLabel(day)}
            onChange={pickDay}
            placeholder='Day'
            small
          />
        ) : (
          <p className='day font-medium'>{dayLabel(day)}</p>
        )}
        {myId && (
          <CheckBox
            label='My games only'
            value={onlyMine}
            onChange={setOnlyMine}
            small
          />
        )}
        <ul className='legend'>
          {legend.map(([division, { color }]) => (
            <li key={division} style={{ borderLeftColor: color }}>
              {division}
            </li>
          ))}
        </ul>
        {canAssign && (
          <button
            className='small bw icon fa-comments-o assistant-toggle'
            onClick={() => setIsAssistantOpen(true)}
          >
            Assistant
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <p className='compact-schedule-empty'>You have no games on this day</p>
      ) : (
        <div className='grid-wrap'>
          <div className='grid' style={{ '--columns': columns.length }}>
            <div className='row head'>
              <div className='time' />
              {columns.map((field) => (
                <p key={field.id} className='cell'>
                  {field.name}
                </p>
              ))}
            </div>
            {rows.map(({ start, games: startGames }) => (
              <div key={start ?? ''} className='row'>
                <p className='time'>{start ? time24To12H(start) : 'TBD'}</p>
                {columns.map((field) => {
                  const here = startGames.filter(
                    (game) => game.field === field.id,
                  );
                  return (
                    <div
                      key={field.id}
                      className={'cell' + (here.length ? '' : ' empty')}
                    >
                      {here.map((game) => (
                        <CompactGame
                          key={game.id}
                          id={id}
                          game={game}
                          fieldName={field.name}
                          colorLegend={colorLegend}
                          acceptedIds={acceptedIds}
                          myId={myId}
                          highlighted={game.id === highlightId}
                          canAssign={canAssign}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (!canAssign) return schedule;

  return (
    <div
      className={
        'schedule-with-assistant' + (isAssistantOpen ? ' assistant-open' : '')
      }
    >
      {schedule}
      <AssistantPanel
        tournamentId={id}
        date={day}
        isOpen={isAssistantOpen}
        onToggle={() => setIsAssistantOpen((open) => !open)}
      />
    </div>
  );
}
